import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Upload, Store, AlertCircle, CheckCircle, Trash2, Plus, Eye, ArrowLeftCircle, Leaf, Drumstick, Edit, PlusCircle, PauseCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BulkImportMenuPage = () => {
  const navigate = useNavigate();
  const { vendorId } = useParams();

  // Guard: vendor_id is mandatory
  if (!vendorId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Vendor ID Missing</h2>
          <p className="text-gray-500 mb-4">Cannot import menu without a vendor. Please select a vendor first.</p>
          <Button onClick={() => navigate('/admin/vendor-registrations')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="go-to-vendors-btn">Go to Vendors</Button>
        </div>
      </div>
    );
  }

  // Get vendor name from URL query params
  const urlParams = new URLSearchParams(window.location.search);
  const vendorName = urlParams.get('name') || 'Unknown Vendor';

  // Phase state: 'paste', 'edit', 'preview', 'published'
  const [phase, setPhase] = useState('paste');

  // State for paste area and parsing
  const [pasteContent, setPasteContent] = useState('');
  const [parsedItems, setParsedItems] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  
  // Publishing state
  const [isPublishing, setIsPublishing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  // Edit Menu mode state
  const [showEditMenu, setShowEditMenu] = useState(false);
  const [editMenuItems, setEditMenuItems] = useState([]);
  const [editMenuLoading, setEditMenuLoading] = useState(false);
  const [toggleStates, setToggleStates] = useState({});

  const fetchEditMenuItems = async () => {
    setEditMenuLoading(true);
    try {
      const response = await axios.get(`${API}/menu?vendor_id=${vendorId}&include_disabled=true`);
      const allItems = [];
      const initToggles = {};
      (response.data.categories || []).forEach(cat => {
        (cat.items || []).forEach(item => {
          allItems.push({ ...item, _categoryName: cat.name });
          initToggles[item.id] = item.is_available !== false;
        });
      });
      setEditMenuItems(allItems);
      setToggleStates(initToggles);
      setShowEditMenu(true);
    } catch (err) {
      toast.error('Failed to load menu items');
    } finally {
      setEditMenuLoading(false);
    }
  };

  const handleToggle = async (itemId) => {
    const newState = !toggleStates[itemId];
    // Optimistic update
    setToggleStates(prev => ({ ...prev, [itemId]: newState }));
    try {
      await axios.patch(`${API}/menu/toggle`, { item_id: itemId, is_active: newState });
    } catch (err) {
      // Revert on failure
      setToggleStates(prev => ({ ...prev, [itemId]: !newState }));
      toast.error('Failed to update item');
    }
  };

  // Normalize Veg/Non-Veg value
  const normalizeVegStatus = (value) => {
    if (!value || value.trim() === '') return null;
    const normalized = value.trim().toLowerCase();
    if (['veg', 'v', 'yes'].includes(normalized)) return 'VEG';
    if (['non-veg', 'nonveg', 'n', 'no'].includes(normalized)) return 'NON_VEG';
    return null;
  };

  // Validate a single item
  const validateItem = (item) => {
    const errors = [];
    if (!item.category || !item.category.trim()) {
      errors.push('Category is required');
    }
    if (!item.itemName || !item.itemName.trim()) {
      errors.push('Item Name is required');
    }
    if (item.price === null || item.price === undefined || item.price === '') {
      errors.push('Price is required');
    } else if (isNaN(parseFloat(item.price)) || parseFloat(item.price) <= 0) {
      errors.push('Price must be a valid number > 0');
    }
    return errors;
  };

  // Parse TSV content
  const parseMenuItems = () => {
    setIsParsing(true);
    try {
      const lines = pasteContent.split('\n');
      const items = [];

      lines.forEach((line, index) => {
        if (!line.trim()) return;
        const columns = line.split('\t');

        let category, subcategory, itemName, priceStr, vegStatus, description;

        if (columns.length >= 6) {
          // New format: Category | Subcategory | Item Name | Price | Veg/Non-Veg | Description
          category = columns[0]?.trim() || 'Uncategorized';
          subcategory = columns[1]?.trim() || null;
          itemName = columns[2]?.trim() || '';
          priceStr = columns[3]?.trim() || '';
          vegStatus = columns[4]?.trim() || '';
          description = columns[5]?.trim() || '';
        } else {
          // Old format: Category | Item Name | Price | Veg/Non-Veg | Description
          category = columns[0]?.trim() || 'Uncategorized';
          subcategory = null;
          itemName = columns[1]?.trim() || '';
          priceStr = columns[2]?.trim() || '';
          vegStatus = columns[3]?.trim() || '';
          description = columns[4]?.trim() || '';
        }

        const price = parseFloat(priceStr);

        const item = {
          id: `item-${Date.now()}-${index}`,
          rowNumber: index + 1,
          category: category || 'Uncategorized',
          subcategory: subcategory || null,
          itemName,
          price: isNaN(price) ? '' : price,
          vegStatus: normalizeVegStatus(vegStatus),
          vegStatusDisplay: vegStatus,
          description,
        };

        const errors = validateItem(item);
        item.isValid = errors.length === 0;
        item.errors = errors;
        items.push(item);
      });

      if (items.length === 0) {
        toast.error('No items to parse');
        setIsParsing(false);
        return;
      }

      const validCount = items.filter(i => i.isValid).length;
      setParsedItems(items);
      setPhase('edit');
      toast.success(`Parsed ${items.length} items (${validCount} valid)`);
    } catch (error) {
      toast.error('Parse error');
    } finally {
      setIsParsing(false);
    }
  };

  // Update item field
  const updateItem = (id, field, value) => {
    setParsedItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      const updated = { ...item, [field]: value };
      if (field === 'vegStatusDisplay') {
        updated.vegStatus = normalizeVegStatus(value);
      }
      const errors = validateItem(updated);
      updated.isValid = errors.length === 0;
      updated.errors = errors;
      return updated;
    }));
  };

  // Delete item
  const deleteItem = (id) => {
    setParsedItems(prev => prev.filter(item => item.id !== id));
    toast.success('Row deleted');
  };

  // Add new row
  const addNewRow = () => {
    const newItem = {
      id: `item-${Date.now()}-new`,
      rowNumber: parsedItems.length + 1,
      category: 'Uncategorized',
      subcategory: null,
      itemName: '',
      price: '',
      vegStatus: null,
      vegStatusDisplay: '',
      description: '',
      isValid: false,
      errors: ['Item Name is required', 'Price is required']
    };
    setParsedItems(prev => [...prev, newItem]);
  };

  // Back to paste phase
  const backToPaste = () => {
    if (parsedItems.length > 0) {
      if (window.confirm('Going back will clear your edits. Continue?')) {
        setPhase('paste');
      }
    } else {
      setPhase('paste');
    }
  };

  // Calculate stats
  const validItems = parsedItems.filter(i => i.isValid);
  const invalidItems = parsedItems.filter(i => !i.isValid);

  // Go to Preview phase
  const handlePreview = () => {
    if (validItems.length === 0) {
      toast.error('No valid items to preview');
      return;
    }
    if (invalidItems.length > 0) {
      if (!window.confirm(`You have ${invalidItems.length} invalid row(s). Preview only ${validItems.length} valid item(s)?`)) {
        return;
      }
    }
    setPhase('preview');
  };

  // Group items by category for preview
  const getGroupedItems = () => {
    const grouped = {};
    validItems.forEach(item => {
      const cat = item.category || 'Uncategorized';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });
    return grouped;
  };

  // Publish menu to database
  const handlePublish = async () => {
    setShowConfirmModal(false);
    setIsPublishing(true);

    try {
      // Transform items to backend format
      const itemsToPublish = validItems.map(item => ({
        item_name: item.itemName,
        price: parseFloat(item.price),
        category: item.category || 'Uncategorized',
        subcategory: item.subcategory || null,
        description: item.description || '',
        is_veg: item.vegStatus === 'VEG' ? true : item.vegStatus === 'NON_VEG' ? false : null
      }));

      const response = await axios.post(`${API}/menu/bulk-create`, {
        vendor_id: vendorId,
        items: itemsToPublish
      });

      if (response.data.success) {
        setPublishResult({
          success: true,
          count: response.data.created_count,
          categories_created: response.data.categories_created || 0,
          duplicates_skipped: response.data.duplicates_skipped || 0,
          rows_skipped: response.data.rows_skipped || 0,
          message: response.data.message,
          errors: response.data.errors
        });
        setPhase('published');
        toast.success(`Menu published! ${response.data.created_count} items added.`);
      } else {
        toast.error('Failed to publish menu');
        setPublishResult({
          success: false,
          message: response.data.message || 'Failed to publish'
        });
      }
    } catch (error) {
      console.error('Publish error:', error);
      toast.error('Failed to publish menu');
      setPublishResult({
        success: false,
        message: error.response?.data?.detail || 'Server error'
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // ==================== RENDER ====================

  // PHASE: PASTE
  if (phase === 'paste') {
    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate('/admin/vendor-registrations')} className="p-2 hover:bg-white/20 rounded-full" data-testid="back-button">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold">Bulk Import Menu</h1>
                <p className="text-blue-100">Import menu items in bulk</p>
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
              <Store className="w-8 h-8" />
              <div>
                <p className="text-lg font-semibold">{vendorName}</p>
                <p className="text-sm text-blue-100">Vendor ID: {vendorId}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="mb-6">
              <label className="block text-lg font-bold text-gray-800 mb-2">Paste menu items below</label>
              <p className="text-sm text-gray-600 mb-3"><span className="font-medium">Format</span> (TAB separated – copy directly from Excel / Google Sheets):</p>
              <p className="text-sm text-gray-500 mb-1 font-mono bg-gray-50 px-3 py-2 rounded border">Category | Subcategory | Item Name | Price | Veg/Non-Veg (optional) | Description</p>
              <p className="text-xs text-gray-400 mb-4">Subcategory is optional. Old format (5 columns without subcategory) also works.</p>
              <div className="mb-4 p-4 bg-gray-100 rounded-lg border border-gray-200">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Example (for reference only):</p>
                <pre className="text-sm text-gray-500 font-mono whitespace-pre-wrap overflow-x-auto">{`Paneer	Paneer Kolhapuri	240	Veg	Spicy Kolhapuri style paneer curry
Paneer	Paneer Butter Masala	260	Veg	Creamy tomato gravy
Rice	Veg Biryani	220		Long grain rice with spices`}</pre>
                <p className="text-xs text-gray-400 mt-2 italic">Note: Veg/Non-Veg column can be left empty. Columns are TAB-separated.</p>
              </div>
              <textarea value={pasteContent} onChange={(e) => setPasteContent(e.target.value)} placeholder="Paste your menu items here (from Excel or Google Sheets)..." className="w-full h-64 p-4 border border-gray-300 rounded-lg font-mono text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-testid="paste-textarea" />
            </div>
            <Button onClick={parseMenuItems} disabled={isParsing || !pasteContent.trim()} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 text-lg font-semibold rounded-xl flex items-center justify-center gap-2" data-testid="parse-button">
              {isParsing ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Parsing...</>) : (<><Upload className="w-5 h-5" />Parse / Load Items</>)}
            </Button>
            <Button
              onClick={fetchEditMenuItems}
              disabled={editMenuLoading}
              variant="outline"
              className="w-full mt-3 border-gray-300 text-gray-700 py-4 text-lg font-semibold rounded-xl flex items-center justify-center gap-2"
              data-testid="edit-menu-button"
            >
              {editMenuLoading ? (<><div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Loading...</>) : (<><Edit className="w-5 h-5" />Edit Menu</>)}
            </Button>
          </div>

          {/* Edit Menu Items Section */}
          {showEditMenu && (
            <div className="bg-white rounded-xl shadow-md p-6 mt-6" data-testid="edit-menu-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-gray-800">Edit Menu Items</h2>
                <button onClick={() => setShowEditMenu(false)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
              </div>

              {editMenuItems.length === 0 ? (
                <p className="text-gray-400 text-center py-8">No menu items found for this vendor.</p>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto space-y-2">
                  {editMenuItems.map((item) => {
                    const isOn = toggleStates[item.id] !== false;
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-opacity ${isOn ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-50'}`}
                        data-testid={`edit-item-${item.id}`}
                      >
                        <div className="flex-1 min-w-0 mr-4">
                          <p className={`font-medium text-sm truncate ${isOn ? 'text-gray-800' : 'text-gray-400'}`}>{item.name}</p>
                          {item._categoryName && <p className="text-xs text-gray-400 mt-0.5">{item._categoryName}</p>}
                        </div>
                        <span className={`text-sm font-semibold mr-4 whitespace-nowrap ${isOn ? 'text-gray-800' : 'text-gray-400'}`}>
                          ₹{item.price}
                        </span>
                        <button
                          onClick={() => handleToggle(item.id)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${isOn ? 'bg-green-500' : 'bg-gray-300'}`}
                          data-testid={`toggle-${item.id}`}
                        >
                          <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${isOn ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-3 text-center">Changes are saved instantly. Disabled items won't appear in the customer app.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // PHASE: EDIT
  if (phase === 'edit') {
    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate('/admin/vendor-registrations')} className="p-2 hover:bg-white/20 rounded-full" data-testid="back-button">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold">Bulk Import Menu</h1>
                <p className="text-blue-100">Review and edit menu items</p>
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
              <Store className="w-8 h-8" />
              <div>
                <p className="text-lg font-semibold">{vendorName}</p>
                <p className="text-sm text-blue-100">Vendor ID: {vendorId}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="bg-white rounded-xl shadow-md p-4 mb-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-6">
              <div className="text-center"><p className="text-2xl font-bold text-gray-800">{parsedItems.length}</p><p className="text-xs text-gray-500 uppercase">Total Rows</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-green-600">{validItems.length}</p><p className="text-xs text-gray-500 uppercase">Valid</p></div>
              <div className="text-center"><p className="text-2xl font-bold text-red-600">{invalidItems.length}</p><p className="text-xs text-gray-500 uppercase">Invalid</p></div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={backToPaste} variant="outline" className="flex items-center gap-2" data-testid="back-to-paste-btn"><ArrowLeftCircle className="w-4 h-4" />Back to Paste</Button>
              <Button onClick={addNewRow} variant="outline" className="flex items-center gap-2" data-testid="add-row-btn"><Plus className="w-4 h-4" />Add Row</Button>
              <Button onClick={handlePreview} disabled={validItems.length === 0} className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2" data-testid="preview-btn"><Eye className="w-4 h-4" />Preview</Button>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-8">#</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]">Category</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[120px]">Subcategory</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[180px]">Item Name *</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[100px]">Price *</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[100px]">Veg/Non-Veg</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[200px]">Description</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {parsedItems.map((item, index) => (
                    <tr key={item.id} className={`transition-colors ${!item.isValid ? 'bg-red-50 border-l-4 border-l-red-500' : 'hover:bg-gray-50'}`} data-testid={`item-row-${index}`}>
                      <td className="px-3 py-2 text-sm text-gray-500">{index + 1}</td>
                      <td className="px-3 py-2"><Input value={item.category} onChange={(e) => updateItem(item.id, 'category', e.target.value)} placeholder="Uncategorized" className="text-sm h-9" /></td>
                      <td className="px-3 py-2"><Input value={item.subcategory || ''} onChange={(e) => updateItem(item.id, 'subcategory', e.target.value.trim() || null)} placeholder="Optional" className="text-sm h-9" /></td>
                      <td className="px-3 py-2"><Input value={item.itemName} onChange={(e) => updateItem(item.id, 'itemName', e.target.value)} placeholder="Item name (required)" className={`text-sm h-9 ${!item.itemName ? 'border-red-300' : ''}`} /></td>
                      <td className="px-3 py-2"><Input type="number" value={item.price} onChange={(e) => updateItem(item.id, 'price', e.target.value)} placeholder="₹" className={`text-sm h-9 ${!item.price || isNaN(parseFloat(item.price)) ? 'border-red-300' : ''}`} /></td>
                      <td className="px-3 py-2">
                        <select value={item.vegStatus || ''} onChange={(e) => { updateItem(item.id, 'vegStatus', e.target.value || null); updateItem(item.id, 'vegStatusDisplay', e.target.value); }} className="w-full text-sm h-9 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                          <option value="">-- Select --</option>
                          <option value="VEG">Veg</option>
                          <option value="NON_VEG">Non-Veg</option>
                        </select>
                      </td>
                      <td className="px-3 py-2"><Input value={item.description} onChange={(e) => updateItem(item.id, 'description', e.target.value)} placeholder="Description (optional)" className="text-sm h-9" /></td>
                      <td className="px-3 py-2 text-center"><button onClick={() => deleteItem(item.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Delete row"><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {invalidItems.length > 0 && (
              <div className="p-4 bg-red-50 border-t border-red-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">{invalidItems.length} row(s) have errors:</p>
                    <ul className="mt-1 text-sm text-red-700 space-y-1">
                      {invalidItems.slice(0, 5).map((item) => (<li key={item.id}>Row {parsedItems.indexOf(item) + 1}: {item.errors.join(', ')}</li>))}
                      {invalidItems.length > 5 && <li className="text-red-500">...and {invalidItems.length - 5} more</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {parsedItems.length === 0 && (<div className="p-8 text-center text-gray-500"><p>No items. Click "Add Row" to add items manually.</p></div>)}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button onClick={handlePreview} disabled={validItems.length === 0} className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg flex items-center gap-2" data-testid="preview-btn-bottom">
              <Eye className="w-5 h-5" />Preview ({validItems.length} items)
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // PHASE: PREVIEW
  if (phase === 'preview') {
    const groupedItems = getGroupedItems();
    const categories = Object.keys(groupedItems);

    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setPhase('edit')} className="p-2 hover:bg-white/20 rounded-full" data-testid="back-to-edit-btn">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold">Final Preview</h1>
                <p className="text-green-100">Review before publishing</p>
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
              <Store className="w-8 h-8" />
              <div>
                <p className="text-lg font-semibold">{vendorName}</p>
                <p className="text-sm text-green-100">Vendor ID: {vendorId}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Summary */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{validItems.length} items ready to publish</h2>
                <p className="text-gray-500">Grouped into {categories.length} {categories.length === 1 ? 'category' : 'categories'}</p>
              </div>
              <Button onClick={() => setPhase('edit')} variant="outline" className="flex items-center gap-2">
                <Edit className="w-4 h-4" />Edit Items
              </Button>
            </div>
          </div>

          {/* Items grouped by category */}
          {categories.map(category => (
            <div key={category} className="bg-white rounded-xl shadow-md mb-4 overflow-hidden">
              <div className="bg-gray-100 px-6 py-3 border-b">
                <h3 className="font-bold text-gray-800">{category}</h3>
                <p className="text-sm text-gray-500">{groupedItems[category].length} items</p>
              </div>
              <div className="divide-y divide-gray-100">
                {groupedItems[category].map(item => (
                  <div key={item.id} className="p-4 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-gray-800">{item.itemName}</h4>
                        {item.vegStatus === 'VEG' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            <Leaf className="w-3 h-3" />Veg
                          </span>
                        )}
                        {item.vegStatus === 'NON_VEG' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                            <Drumstick className="w-3 h-3" />Non-Veg
                          </span>
                        )}
                      </div>
                      {item.description && <p className="text-sm text-gray-500">{item.description}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-800">₹{item.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Publish Button */}
          <div className="mt-6">
            <Button
              onClick={() => setShowConfirmModal(true)}
              disabled={isPublishing}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-4 text-lg font-semibold rounded-xl flex items-center justify-center gap-2"
              data-testid="publish-menu-btn"
            >
              {isPublishing ? (
                <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Publishing...</>
              ) : (
                <><Upload className="w-5 h-5" />Publish Menu</>
              )}
            </Button>
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Publish Menu?</h3>
                <p className="text-gray-500">
                  This will make your menu live for customers. {validItems.length} items will be added to the restaurant menu.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowConfirmModal(false)}
                  variant="outline"
                  className="flex-1"
                  data-testid="cancel-publish-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePublish}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  data-testid="confirm-publish-btn"
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // PHASE: PUBLISHED
  if (phase === 'published') {
    return (
      <div className="min-h-screen bg-gray-50 pb-8">
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate('/admin/vendor-registrations')} className="p-2 hover:bg-white/20 rounded-full" data-testid="back-button">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold">Menu Published!</h1>
                <p className="text-green-100">Your menu is now live</p>
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-4 flex items-center gap-3">
              <Store className="w-8 h-8" />
              <div>
                <p className="text-lg font-semibold">{vendorName}</p>
                <p className="text-sm text-green-100">Vendor ID: {vendorId}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* Success Card */}
          <div className="bg-white rounded-xl shadow-md p-8 text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Menu published successfully!</h2>
            <p className="text-gray-500 mb-2">
              {publishResult?.count || validItems.length} items added to the menu.
            </p>
            {publishResult && (publishResult.categories_created > 0 || publishResult.duplicates_skipped > 0 || publishResult.rows_skipped > 0) && (
              <div className="mt-3 text-sm text-gray-500 space-y-1">
                {publishResult.categories_created > 0 && <p>{publishResult.categories_created} new {publishResult.categories_created === 1 ? 'category' : 'categories'} created</p>}
                {publishResult.duplicates_skipped > 0 && <p>{publishResult.duplicates_skipped} duplicate(s) skipped</p>}
                {publishResult.rows_skipped > 0 && <p>{publishResult.rows_skipped} invalid row(s) skipped</p>}
              </div>
            )}
            <p className="text-sm text-green-600 font-medium">
              Your menu is now live and visible to customers.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="font-semibold text-gray-800 mb-4">What would you like to do next?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => {
                  setParsedItems([]);
                  setPasteContent('');
                  setPhase('paste');
                  setPublishResult(null);
                }}
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors text-left"
                data-testid="add-more-btn"
              >
                <PlusCircle className="w-8 h-8 text-blue-600 mb-2" />
                <h4 className="font-semibold text-gray-800">Add More Items</h4>
                <p className="text-sm text-gray-500">Import additional menu items</p>
              </button>

              <button
                onClick={() => navigate(`/vendor/${vendorId}`)}
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 transition-colors text-left"
                data-testid="view-menu-btn"
              >
                <Eye className="w-8 h-8 text-green-600 mb-2" />
                <h4 className="font-semibold text-gray-800">View Live Menu</h4>
                <p className="text-sm text-gray-500">See your menu as customers see it</p>
              </button>

              <button
                onClick={() => navigate('/admin/vendor-registrations')}
                className="p-4 border-2 border-gray-200 rounded-xl hover:border-gray-500 hover:bg-gray-50 transition-colors text-left"
                data-testid="back-to-vendors-btn"
              >
                <ArrowLeftCircle className="w-8 h-8 text-gray-600 mb-2" />
                <h4 className="font-semibold text-gray-800">Back to Vendors</h4>
                <p className="text-sm text-gray-500">Return to vendor list</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BulkImportMenuPage;
